import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import AdminRoutes from "./AdminRoutes";

import LandingPage from "../pages/LandingPage";
import LoginPage from "../pages/LoginPage";
import SignupPage from "../pages/SignupPage";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import HomePage from "../pages/HomePage";
import CommunityPage from "../pages/CommunityPage";
import PilgrimMembershipPage from "../pages/PilgrimMembershipPage";
import FaithStreamPage from "../pages/FaithStreamPage";
import ProfilePage from "../pages/ProfilePage";
import MessagesPage from "../pages/MessagesPage";
import OtherUserProfileImage from "../pages/OtherUserProfile";
import Mentorship from "../pages/Mentorship";
import NotificationPage from "../pages/NotificationPage";
import InviteHandler from "../pages/InviteHandler";
import PendingRequestsPage from "../pages/PendingRequestPage";
import SuggestedContacts from "../pages/SuggestedContactPage";

// ====================================
// Protected Route
// ====================================

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ✅ Admin Check
  const isAdmin = localStorage.getItem("isAdminLogin") === "true";
  if (isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // ✅ Not Logged In
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // ✅ Role-based Restriction
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect "free" users to community page
    if (user.role === "free") {
      return <Navigate to="/community" replace />;
    }
    // You can add more role restrictions here
    return <Navigate to="/community" replace />;
  }

  return children;
};

// ====================================
// Guest Route
// ====================================

const GuestRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;

  const isAdmin =
    localStorage.getItem("isAdminLogin") === "true";

  // ✅ Redirect Admin
  if (isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // ✅ Redirect User
  if (user) {
    return <Navigate to="/community" replace />;
  }

  return children;
};

// ====================================
// Routes
// ====================================

const AppRoutes = () => (
  <Routes>
    {/* Landing */}
    <Route path="/" element={<LandingPage />} />

    {/* Login */}
    <Route
      path="/login"
      element={
        <GuestRoute>
          <LoginPage />
        </GuestRoute>
      }
    />

    {/* Signup */}
    <Route
      path="/signup"
      element={
        <GuestRoute>
          <SignupPage />
        </GuestRoute>
      }
    />

    {/* Forgot Password */}
    <Route
      path="/forgot-password"
      element={
        <GuestRoute>
          <ForgotPasswordPage />
        </GuestRoute>
      }
    />

    {/* Reset Password */}
    <Route
      path="/reset-password"
      element={<ResetPasswordPage />}
    />

    {/* Home */}
    {/* <Route
      path="/home"
      element={
        <ProtectedRoute>
          <HomePage />
        </ProtectedRoute>
      }
    /> */}



    {/* Community */}
    <Route
      path="/community"
      element={
        <ProtectedRoute allowedRoles={["pilgrim", "free"]}>
          <CommunityPage />
        </ProtectedRoute>
      }
    />

    {/* Membership */}
    <Route
      path="/pilgrim-membership"
      element={
        <ProtectedRoute allowedRoles={["pilgrim", "free"]}>
          <PilgrimMembershipPage />
        </ProtectedRoute>
      }
    />

    {/* Faith Stream */}
    <Route
      path="/faith-stream"
      element={
        <ProtectedRoute allowedRoles={["pilgrim"]}>
          <FaithStreamPage />
        </ProtectedRoute>
      }
    />

    <Route
      path="/mentorship"
      element={
        <ProtectedRoute allowedRoles={["pilgrim"]}>
          <Mentorship />
        </ProtectedRoute>
      }
    />
    <Route path="/invite" element={
      <ProtectedRoute allowedRoles={["pilgrim"]}>
        <InviteHandler />
      </ProtectedRoute>
    } />

    {/* Profile */}
    <Route
      path="/profile"
      element={
        <ProtectedRoute allowedRoles={["pilgrim", "free"]}>
          <ProfilePage />
        </ProtectedRoute>
      }
    />

    {/* Other Profile */}
    <Route
      path="/other-profile/:id"
      element={
        <ProtectedRoute allowedRoles={["pilgrim"]}>
          <OtherUserProfileImage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/notifications"
      element={
        <ProtectedRoute allowedRoles={["pilgrim", "free"]}>
          <NotificationPage />
        </ProtectedRoute>
      }
    />
    {/* Messages */}
    <Route
      path="/messages"
      element={
        <ProtectedRoute allowedRoles={["pilgrim"]}>
          <MessagesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/pending-requests"
      element={
        <ProtectedRoute allowedRoles={["pilgrim"]}>
          <PendingRequestsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/contacts"
      element={
        <ProtectedRoute allowedRoles={["pilgrim"]}>
          <SuggestedContacts />
        </ProtectedRoute>
      }
    />

    {/* Admin Routes */}
    <Route path="/admin/*" element={<AdminRoutes />} />

    {/* Fallback */}
    <Route
      path="*"
      element={<Navigate to="/" replace />}
    />
  </Routes>
);

export default AppRoutes;